import { z } from "zod";

import { ApprovalStatusSchema } from "./guards.js";
import { AttachmentSchema, BlobIdSchema, MimeTypeSchema } from "./parts.js";
import { LoginChallengeSchema, LoginStatusSchema } from "./login.js";

/**
 * The chat: what the user sees, as opposed to the model transcript (entries).
 * The prototype keeps the two apart as well: its transcript entries are the
 * bubbles, its conversation state is the model's view. A user message lands
 * in both; the agent's sends land here and, as the tool call that made
 * them, in the transcript.
 */

export const ReactionSchema = z.object({ emoji: z.string().min(1).max(16), by: z.enum(["agent", "user"]) }).strict();
export type Reaction = z.infer<typeof ReactionSchema>;

/** An image the agent put under its text. */
export const ChatImageSchema = z.object({ blobId: BlobIdSchema, mimeType: MimeTypeSchema, alt: z.string().optional() }).strict();
export type ChatImage = z.infer<typeof ChatImageSchema>;

/** A file the agent sent on its own; images render inline, anything else as a download. */
export const ChatFileSchema = z.object({ blobId: BlobIdSchema, mimeType: MimeTypeSchema, name: z.string().min(1), size: z.number().int().nonnegative() }).strict();
export type ChatFile = z.infer<typeof ChatFileSchema>;

const base = {
  /** Short and stable, e.g. `u12` / `a13`: the model addresses messages by it. */
  id: z.string().min(1),
  seq: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
};

/**
 * The user wrote from their own phone on a channel rather than in the app
 * (§6.19), or the agent's reply went back there as well. The chat is the same
 * conversation either way; the mark says where each line travelled.
 */
const via = z.enum(["whatsapp"]).optional();

export const UserChatEntrySchema = z
  .object({ kind: z.literal("user"), ...base, text: z.string(), attachments: z.array(AttachmentSchema), reactions: z.array(ReactionSchema).optional(), via, replyTo: z.string().optional() })
  .strict();
export type UserChatEntry = z.infer<typeof UserChatEntrySchema>;

export const AgentChatEntrySchema = z
  .object({ kind: z.literal("agent"), ...base, text: z.string().optional(), images: z.array(ChatImageSchema).optional(), file: ChatFileSchema.optional(), replyTo: z.string().optional(), via })
  .strict();
export type AgentChatEntry = z.infer<typeof AgentChatEntrySchema>;

/**
 * An approval card: a guard asked before one of the agent's actions ran, and
 * the card sits in the chat until the user answers it or it expires, as the
 * prototype's Auto-review card does. `detail` is the exact action (a command,
 * a URL, a prompt) for the user to read before deciding.
 */
export const ApprovalChatEntrySchema = z
  .object({
    kind: z.literal("approval"),
    ...base,
    approvalId: z.string().min(1),
    tool: z.string().min(1),
    summary: z.string().min(1),
    reason: z.string().min(1),
    detail: z.string().optional(),
    /** A rule the classifier proposed for the always-allow list, which the card offers with the approval. */
    proposedRule: z.string().min(1).optional(),
    status: ApprovalStatusSchema,
    expiresAt: z.number().int().nonnegative().optional(),
  })
  .strict();
export type ApprovalChatEntry = z.infer<typeof ApprovalChatEntrySchema>;

/**
 * How a hand-off ended. `waiting` while the user has the box; `handed_back`
 * when they said they were done, `skipped` when they declined the step, and
 * `replied` when they answered in the chat instead of pressing either button.
 */
export const HandoffStatusSchema = z.enum(["waiting", "handed_back", "skipped", "replied"]);
export type HandoffStatus = z.infer<typeof HandoffStatusSchema>;

/** How the user ended a hand-off; the chat answers with `replied` on its own. */
export const HandoffResolutionSchema = z.enum(["handed_back", "skipped"]);
export type HandoffResolution = z.infer<typeof HandoffResolutionSchema>;

/**
 * The agent asking for the box: a step only the user can do, such as a login,
 * a passkey or a captcha. The card carries the instruction and sits in the
 * chat until they hand the box back, as the prototype's computer hand-off card
 * does; the same request is drawn over the screen in the computer pane.
 */
export const HandoffChatEntrySchema = z
  .object({ kind: z.literal("handoff"), ...base, instruction: z.string().min(1), status: HandoffStatusSchema, display: z.string().min(1).optional() })
  .strict();
export type HandoffChatEntry = z.infer<typeof HandoffChatEntrySchema>;

/**
 * One agent writing to another (ARCHITECTURE §6.7). The same message lands in
 * both chats: `out` in the sender's, `in` in the recipient's, each naming the
 * agent at the other end, so the user watches the exchange from either side.
 * The prototype does the same, as an assistant bubble with a `toAgent` on one
 * side and a user bubble with a `fromAgent` on the other.
 */
export const PeerChatEntrySchema = z
  .object({ kind: z.literal("peer"), ...base, direction: z.enum(["in", "out"]), peer: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict(), text: z.string().min(1) })
  .strict();
export type PeerChatEntry = z.infer<typeof PeerChatEntrySchema>;

/**
 * A member speaking in a group chat (ARCHITECTURE §6.8). The room's chat is
 * the user's messages and these; each says which member sent it, because
 * several agents share the one conversation.
 */
export const RoomChatEntrySchema = z
  .object({ kind: z.literal("room"), ...base, author: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict(), text: z.string().min(1) })
  .strict();
export type RoomChatEntry = z.infer<typeof RoomChatEntrySchema>;

/** How a field is shown, and so what the answer looks like coming back. */
export const FormFieldTypeSchema = z.enum(["text", "textarea", "number", "date", "select", "checkbox"]);
export type FormFieldType = z.infer<typeof FormFieldTypeSchema>;

export const FormFieldSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    type: FormFieldTypeSchema,
    required: z.boolean().optional(),
    /** The choices, for a select. */
    options: z.array(z.string().min(1)).optional(),
    placeholder: z.string().optional(),
  })
  .strict();
export type FormField = z.infer<typeof FormFieldSchema>;

export const FormStatusSchema = z.enum(["waiting", "submitted", "cancelled", "expired"]);
export type FormStatus = z.infer<typeof FormStatusSchema>;

/**
 * The agent asking several things at once (ARCHITECTURE §6.12): a card of
 * typed fields the user fills in, whose answers go back to the agent as its
 * tool result. `values` is filled in once it has been submitted, so the card
 * keeps showing what was answered.
 */
export const FormChatEntrySchema = z
  .object({
    kind: z.literal("form"),
    ...base,
    formId: z.string().min(1),
    title: z.string().min(1),
    note: z.string().optional(),
    fields: z.array(FormFieldSchema).min(1),
    status: FormStatusSchema,
    values: z.record(z.string(), z.string()).optional(),
    expiresAt: z.number().int().nonnegative().optional(),
  })
  .strict();
export type FormChatEntry = z.infer<typeof FormChatEntrySchema>;

/**
 * Someone on a channel, passed on to the user by the agent (§6.19): a message
 * slip, a question the agent needs answered before it can reply to them, or a
 * meeting time they picked, which the agent holds until the user books it
 * (`answered`) or declines it (`dismissed`). The user answers on the card; the
 * answer goes to that person's thread and nowhere else. `status` is `open`
 * until then.
 */
export const ContactChatEntrySchema = z
  .object({
    kind: z.literal("contact"),
    ...base,
    platform: z.enum(["whatsapp"]),
    threadId: z.string().min(1),
    contact: z.object({ name: z.string().min(1), number: z.string().optional() }).strict(),
    mode: z.enum(["message", "question", "meeting"]),
    text: z.string().min(1),
    urgent: z.boolean().optional(),
    status: z.enum(["open", "answered", "dismissed"]),
    answer: z.string().optional(),
    /** For a meeting: the time held, in epoch ms, and what it is about in their words. */
    meeting: z.object({ start: z.number().int().nonnegative(), end: z.number().int().nonnegative(), topic: z.string().min(1) }).strict().optional(),
    /** For a booked meeting: the event in the user's calendar, when the calendar gave a link. */
    eventLink: z.string().optional(),
  })
  .strict();
export type ContactChatEntry = z.infer<typeof ContactChatEntrySchema>;

/**
 * The host speaking, not the agent: a turn ended on an error and nothing else
 * would tell the user, as when the model refused the request. It is only for
 * the user to read; the model never sees it.
 */
export const NoticeChatEntrySchema = z.object({ kind: z.literal("notice"), ...base, text: z.string().min(1) }).strict();
export type NoticeChatEntry = z.infer<typeof NoticeChatEntrySchema>;

export const LoginChatEntrySchema = z.object({ kind: z.literal("login"), ...base, loginId: z.string().min(1), revision: z.number().int().nonnegative(), challenge: LoginChallengeSchema, status: LoginStatusSchema, expiresAt: z.number().int().nonnegative() }).strict();
export type LoginChatEntry = z.infer<typeof LoginChatEntrySchema>;

export const ChatEntrySchema = z.discriminatedUnion("kind", [UserChatEntrySchema, AgentChatEntrySchema, ApprovalChatEntrySchema, HandoffChatEntrySchema, PeerChatEntrySchema, RoomChatEntrySchema, FormChatEntrySchema, ContactChatEntrySchema, NoticeChatEntrySchema, LoginChatEntrySchema]);
export type ChatEntry = z.infer<typeof ChatEntrySchema>;

/**
 * GET /agents/:id/chat: the bubbles plus the event cursor they were read at.
 * With `?limit=`, the newest that many (and `?before=<seq>` for the page
 * above them), `hasMore` saying whether older ones remain; a long chat is
 * read a page at a time from the bottom, as the prototype reads its
 * transcript by tail and window. `screens` and `busy` are the agent's state
 * at that moment, so a client opening `/stream?after=eventSeq` starts right.
 */
/** What the agent's last model call cost against its window: the prompt it was sent and the answer it gave, and the window it condenses at. */
export const ContextUsageSchema = z.object({ inputTokens: z.number().int().nonnegative(), outputTokens: z.number().int().nonnegative(), windowTokens: z.number().int().positive() }).strict();
export type ContextUsage = z.infer<typeof ContextUsageSchema>;

export const ChatResponseSchema = z.object({
  entries: z.array(ChatEntrySchema),
  eventSeq: z.number().int().nonnegative(),
  busy: z.boolean(),
  hasMore: z.boolean().optional(),
  /** For an agent, how full its context is after the last call; a group has none. */
  context: ContextUsageSchema.optional(),
  screens: z.array(z.object({ display: z.string().min(1), title: z.string().min(1), busy: z.boolean() }).strict()).optional(),
}).strict();
export type ChatResponse = z.infer<typeof ChatResponseSchema>;
